import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './lightbox-media.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import type { LightboxImageFitType, LightboxMediaItem } from 'components/discovery/pdp/lightbox/interfaces.js'
import 'components/discovery/pdp/lightbox/components/lightbox-zoom/lightbox-zoom.js'

initializeIconsByRole({
  'csc-lightbox-media.play': { name: 'play', style: 'light' },
})

@customElement('csc-lightbox-media')
export class CSCLightboxMedia extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Object })
  item!: LightboxMediaItem

  @property({ type: Boolean, attribute: 'with-zoom' })
  withZoom = false

  @property({ type: Boolean, attribute: 'autoplay-video' })
  autoplayVideo = false

  @property({ type: String, attribute: 'image-fit' })
  imageFit: LightboxImageFitType = 'cover'

  @state()
  private videoActivated = false

  protected override willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('item')) {
      this.videoActivated = false
    }
  }

  private activateVideo = () => {
    this.videoActivated = true
  }

  private renderImage() {
    if (this.withZoom) {
      return html`
        <csc-lightbox-zoom
          source="${this.item.source}"
          alt="${this.item.alt}"
          fallback-source="${this.item.fallbackSource ?? ''}"
        ></csc-lightbox-zoom>
      `
    }

    return html`
      <cse-image
        source="${this.item.source}"
        alt="${this.item.alt}"
        fit="${this.imageFit}"
        loading="lazy"
        fallback-source="${this.item.fallbackSource ?? nothing}"
      ></cse-image>
    `
  }

  private get embedSource(): string {
    const src = this.item.embedSrc ?? ''
    if (!src) return src

    const isMobile = window.matchMedia(`(max-width: ${breakpoints.small}px)`).matches
    if (!isMobile) return src

    const separator = src.includes('?') ? '&' : '?'
    return `${src}${separator}mute=1&muted=1`
  }

  private renderVideo() {
    if (!this.videoActivated) {
      return this.renderVideoPoster()
    }

    if (this.item.embedSrc) {
      return html`
        <cse-video
          embed-src="${this.embedSource}"
          title="${this.item.alt}"
        ></cse-video>
      `
    }

    if (this.item.sources?.length) {
      return html`
        <cse-video
          .sources="${this.item.sources}"
          title="${this.item.alt}"
          width="auto"
          height="auto"
          ?autoplay="${this.autoplayVideo}"
        ></cse-video>
      `
    }

    return nothing
  }

  private renderVideoPoster() {
    return html`
      <button
        class="video-poster"
        type="button"
        @click="${this.activateVideo}"
        aria-label="${t('play_video_label', { ns: 'csc-lightbox-media', default: 'Play video' })}"
      >
        ${this.item.source ? html`
          <cse-image
            source="${this.item.source}"
            alt="${this.item.alt}"
            fit="cover"
            loading="lazy"
            fallback-source="${this.item.fallbackSource ?? nothing}"
          ></cse-image>
        ` : nothing}
        <span class="video-poster__overlay">
          <cse-icon-button variant="primary" size="default">
            <cse-icon icon-role="csc-lightbox-media.play"></cse-icon>
          </cse-icon-button>
        </span>
      </button>
    `
  }

  override render() {
    if (!this.item) {
      return nothing
    }

    return this.item.kind === 'video' ? this.renderVideo() : this.renderImage()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-lightbox-media': CSCLightboxMedia
  }
}
